import React, { useRef, useState } from 'react';

import type { TocStyle } from '@aline/common';
import { AlineTransforms, isDocumentElement, TOC_STYLES } from '@aline/common';
import { Paper, Popover, Typography } from '@mui/material';
import { AlineTooltip, SelectButton } from 'src/components';
import { DottedTocIcon } from 'src/components/icons/DottedTocIcon';
import { LinksTocIcon } from 'src/components/icons/LinksTocIcon';
import { PlainTocIcon } from 'src/components/icons/PlainTocIcon';
import { SmallDownArrowIcon } from 'src/components/icons/SmallDownArrowIcon';
import { TableOfContentsIcon } from 'src/components/icons/TableOfContentsIcon';
import { useGetSelBoundaryEntry } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql/hooks';
import { useEditor } from 'src/layout-engine';
import { useEditingDisabled, useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  arrowIcon: {
    height: 12,
    marginLeft: theme.spacing(0.5),
    width: 12,
  },
  buttonText: {
    marginLeft: theme.spacing(0.5),
  },
  option: {
    '&:hover, &:active, &:focus-visible': {
      '& [data-toc-card]': {
        stroke: theme.appColors.primary,
      },
    },
    background: 'none',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    display: 'block',
    padding: 0,
  },
  options: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
  },
  selectButton: {
    height: 32,
  },
}));

export function TableOfContentsButton(): JSX.Element {
  const { classes } = useStyles();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const disabled = useIsTocInsertDisabled();
  const { editingMode } = useEditingMode();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const tocStyleOptions = [
    {
      ariaLabel: 'Insert a plain-text table of contents',
      icon: <PlainTocIcon />,
      label: 'Plain text',
      style: TOC_STYLES.plain,
    },
    {
      ariaLabel: 'Insert a dotted table of contents',
      icon: <DottedTocIcon />,
      label: 'Dotted',
      style: TOC_STYLES.dotted,
    },
    {
      ariaLabel: 'Insert a table of contents with links',
      icon: <LinksTocIcon />,
      label: 'Links',
      style: TOC_STYLES.links,
    },
  ];

  function handleInsert(tocStyle: TocStyle): void {
    if (!currentUserId || !editingMode) return;

    setPopoverOpen(false);
    AlineTransforms.insertToc(editor, {
      authorId: currentUserId,
      editingMode,
      tocStyle,
    });
    ClientTransforms.focus(editor);
  }

  return (
    <>
      <SelectButton
        ref={buttonRef}
        aria-haspopup="dialog"
        className={classes.selectButton}
        disabled={disabled}
        selected={popoverOpen}
        onClick={() => setPopoverOpen(!popoverOpen)}
      >
        <TableOfContentsIcon />
        <Typography className={classes.buttonText} color="inherit" variant="caption">
          Table of Contents
        </Typography>
        <SmallDownArrowIcon className={classes.arrowIcon} />
      </SelectButton>
      <Popover
        anchorEl={buttonRef.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={popoverOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={() => setPopoverOpen(false)}
      >
        <Paper>
          <div className={classes.options}>
            {tocStyleOptions.map(option => (
              <AlineTooltip key={option.style} arrow placement="bottom" title={option.label}>
                <button
                  aria-label={option.ariaLabel}
                  className={classes.option}
                  type="button"
                  onClick={() => handleInsert(option.style)}
                >
                  {option.icon}
                </button>
              </AlineTooltip>
            ))}
          </div>
        </Paper>
      </Popover>
    </>
  );
}

function useIsTocInsertDisabled() {
  const editingDisabled = useEditingDisabled();
  const selBoundaryEntry = useGetSelBoundaryEntry();
  const selBoundaryNode = selBoundaryEntry?.[0];

  return editingDisabled || !selBoundaryNode || !isDocumentElement(selBoundaryNode);
}
